import { JobStatus } from '@upright/common/internal/jobs/types';
import { BlanketSourceId } from '@upright/common/model/input/blanket/blanket-product-set/types';
import { assertHierarchyValid } from '@upright/common/model/input/blanket/blanket-product/hierarchy-utils';
import {
  PiuDataState,
  PiuProductState,
  ProductMatchStatus,
} from '@upright/common/model/input/companies/products/product-input-ui/types';
import { Company } from '@upright/common/model/input/companies/types';
import { useCompany } from '@upright/ui/api/model/input/companies';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';
import { useHasProductSegmentsAccess } from '@upright/ui/api/iam/privilege/query-options';
import { cloneDeep, isEqual } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from '@upright/common/utils/uuid';
import { CustomGtmEvent } from '../../../../common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import {
  UpsertProductSetMutationParams,
  UpsertProductSetResponse,
  useUpsertProductSetMutation,
} from '../../onboarding-wizard/select-products/query-hooks';
import { useEventTracking } from '../tracking';
import { buildPiuProducts } from './build-piu-products';
import { fetchProductMatch } from './query-hooks';
import { hasAttributesFromFiles } from '@upright/common/model/input/companies/products/product-input-ui/attributes-from-files';
import { SegmentViewMode, useSegmentViewPreferences } from './segment-view-preferences';
import { ProductEditingContext } from './types';
import { usePlgCompanyEditableProductSet } from './use-editable-product-set';

export { ProductEditingContext };

/**
 * PLT-5109: product sets produced by the modern, user-facing flows that are meant to be edited
 * here — the product input UI itself, and the automatic product set generation (company mapping).
 * Any other provenance (annual-report collector, AutoGPT, SEC EDGAR, segment splits, manual
 * pairing, GPT-as-knowledgebase, …) is an *old* "blanketti" set that predates these flows and is
 * rendered read-only. A set with no source metadata at all is also treated as old → read-only.
 */
export function isEditableBlanketSourceId(sourceId: BlanketSourceId | undefined): boolean {
  return sourceId === BlanketSourceId.PRODUCT_INPUT_UI || sourceId === BlanketSourceId.COMPANY_MAPPING_SERVICE;
}

export interface ProductInputUIState {
  data: PiuDataState | undefined;
  updateProduct: (updatedProduct: PiuProductState) => void;
  duplicateProduct: (product: PiuProductState) => void;
  deleteProduct: (product: PiuProductState) => void;
  /** Move a node (leaf or segment) under a new parent (`null` = top-level). Hierarchy-view DnD (PLT-5690). */
  reparentProduct: (productId: string, newParentId: string | null) => void;
  /** Show the "Matching" column with the matched product name. Team users only. */
  showMatching: boolean;
  setShowMatching: (showMatching: boolean) => void;
  /** Show the "Attributes" column with IPR attributes. Enterprise + team users. */
  showAttributes: boolean;
  setShowAttributes: (showAttributes: boolean) => void;
  /**
   * Whether the products are rendered as a flat list (default, today's
   * behavior) or a read-only indented hierarchy view (PLT-4748). Persisted
   * per-user / per-company via `SegmentViewPreferences`.
   */
  viewMode: SegmentViewMode;
  setViewMode: (viewMode: SegmentViewMode) => void;
  addProduct: (title: string, description: string, revenueShare: number | undefined, parentId?: string | null) => void;
  /** Create a new segment / business-unit container under `parentId` (`null` = top-level). Gated editor only (PLT-5691). */
  addSegment: (parentId: string | null) => void;
  /** Insert a new segment as the PARENT of `productId`, reparenting the product beneath it. Gated editor only (PLT-5707). */
  addParentSegment: (productId: string) => void;
  clearAll: () => void;
  resetChanges: () => void;
  publishAndComputeResults: () => void;
  jobStatus: JobStatus | undefined;
  jobQueryStatus: 'pending' | 'success' | 'error' | 'idle';
  finishStatus: 'pending' | 'success' | 'error' | 'idle';
  autoSaveStatus: 'pending' | 'success' | 'error' | 'idle';
  disabled: boolean;
  /**
   * PLT-5109: true when the resolved product set is an *old* "blanketti" set — produced by
   * neither the product input UI nor the automatic product set generation (company mapping).
   * Folded into `disabled` (so controls grey out + show a tooltip) and additionally used to
   * *hide* the per-product edit modal and the matching/attributes view. Only ever true in
   * dashboard mode — the onboarding review step is always editable.
   */
  readOnly: boolean;
  isLoadingProductSet: boolean;
  /**
   * PLT-4748 (PR 2): Explicit Edit / Save / Cancel paradigm for the
   * Dashboard "Update products" surface. Mirrors the UX of the
   * Operational data and Financial statement input pages (PR #6875):
   * the page is read-only by default and the user clicks Edit to enter
   * an unsaved editing session that ends with Save or Cancel.
   *
   * In `onboarding` mode this stays `true` from mount onwards — the
   * wizard is implicitly an editing flow and continues to use autosave,
   * so the Edit / Save / Cancel buttons are never rendered there.
   */
  isEditing: boolean;
  /**
   * True when the user is in an editing session and `data` differs from
   * the baseline snapshot taken on `startEditing`. Drives the view-switch
   * confirm, the beforeunload guard, and the Save button enablement.
   * Always `false` outside `isEditing`.
   */
  hasUnsavedEdits: boolean;
  /**
   * True when the DMA results are out of date w.r.t. the current product set — drives the
   * "Recompute results" nudge and button. Reflects the delta from the baseline (a change made and
   * then reverted nets to nothing), plus any `'draft'` status carried over from before this edit
   * session (saved but not yet recomputed). PLT-5799.
   */
  needsRecompute: boolean;
  /** Enter edit mode and snapshot the current product set as the baseline. */
  startEditing: () => void;
  /**
   * Persist the in-progress edits to the server and exit edit mode. The
   * optional `onSaved` callback runs after the save succeeds and refetches —
   * used to chain a recompute onto a save (the "Save & recompute" path).
   */
  saveEdits: (onSaved?: () => void) => void;
  /** Discard the in-progress edits, restore baseline, and exit edit mode. */
  cancelEditing: () => void;
  /** Status of the explicit Save mutation (Dashboard mode); idle in onboarding. */
  saveStatus: 'pending' | 'success' | 'error' | 'idle';
  /**
   * The error of the last failed explicit Save (Dashboard mode), for the editor to render; a
   * failed save keeps the user in edit mode with their changes intact, so without this the
   * failure is invisible (PLT-6599). Cleared when an editing session starts or is cancelled, and
   * by the next save attempt. Always `undefined` in onboarding mode.
   */
  saveError: Error | undefined;
}

function getEmptyData(): PiuDataState {
  return {
    products: [],
    sync: false,
  };
}

function useLoadAndSetEditableProductSet(
  companyId: string,
  setCompanyData: Dispatch<SetStateAction<PiuDataState | undefined>>,
  onLoad?: (data: PiuDataState | undefined) => void,
) {
  const productSetQuery = usePlgCompanyEditableProductSet(companyId);
  useEffect(() => {
    const blanket = productSetQuery.data;
    const error = productSetQuery.isError;
    // PLT-5109: an *old* set — one produced by neither the product input UI nor the automatic
    // product set generation (company mapping) — is rendered read-only. Newer company-mapping
    // sets (incl. ones a customer kept unedited after onboarding) stay editable. Only mark
    // read-only once the set has loaded — while `blanket` is still undefined we must not flash
    // the read-only state onto an editable set. Computed before building products because the
    // revenue-share normalization differs between read-only and editable sets.
    const readOnly = blanket ? !isEditableBlanketSourceId(blanket.sourceMetadata?.sourceId) : false;
    const products = blanket && blanket.products ? buildPiuProducts(blanket.products, readOnly) : [];
    if (error || (productSetQuery.isSuccess && !blanket)) {
      const emptyData = getEmptyData();
      setCompanyData(emptyData);
      onLoad?.(emptyData);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    } else if (products) {
      const nextData: PiuDataState = {
        products,
        sync: false,
        blanketStatus: blanket?.status,
        readOnly,
      };
      setCompanyData(nextData);
      onLoad?.(nextData);
    }
  }, [productSetQuery.data, productSetQuery.isError, productSetQuery.isSuccess, setCompanyData, onLoad]);
  return { refetch: productSetQuery.refetch, isLoading: productSetQuery.isPending };
}

function useMatchProducts(
  companyId: string,
  companyData: PiuDataState | undefined,
  company: Company | undefined,
  setProduct: (product: PiuProductState, sync: boolean) => void,
) {
  const companyDataRef = useRef(companyData);

  useEffect(() => {
    companyDataRef.current = companyData;
  }, [companyData]);

  const matchProduct = useCallback(
    async (updatedProduct: PiuProductState) => {
      if (!companyDataRef.current) {
        console.error('Unexpectedly no data in matchProduct');
        return;
      }
      setProduct({ ...updatedProduct, productMatchStatus: ProductMatchStatus.RUNNING }, false);
      try {
        const result = await fetchProductMatch({
          companyName: company?.name || '',
          companyOverview: company?.descriptions[0]?.text || '',
          title: updatedProduct.title,
          description: updatedProduct.description,
          companyWebsite: company?.websiteURL || '',
          // The id the editor was opened for, not `company?.id`: pending rows are matched as soon as
          // the set loads, which can be before `company` has, and without an id the server can't tell
          // the company's products came from files and searches the web for their attributes.
          companyId,
          attributesFromFiles: hasAttributesFromFiles(updatedProduct),
        });
        // PLT-7015: no `ipr` in the result means the row's own attributes, set from files, are kept.
        const ipr = result.ipr ?? updatedProduct.ipr;
        if (!ipr) {
          throw Error('Unexpectedly no IPR in matchProduct result');
        }
        const enrichedProduct: PiuProductState = {
          ...updatedProduct,
          ipr,
          productMatchResult: result.productMatch,
        };
        if (companyDataRef.current.blanketStatus === 'publishing') {
          // eslint-disable-next-line no-console
          console.log('Skipping setProduct because blanket status is publishing', companyDataRef.current);
          return;
        } else {
          setProduct({ ...enrichedProduct, productMatchStatus: ProductMatchStatus.SUCCESS }, true);
        }
      } catch (error) {
        console.error('Failed to fetch product match:', error);
        if (companyDataRef.current.blanketStatus === 'publishing') {
          // eslint-disable-next-line no-console
          console.log('Skipping setProduct because blanket status is publishing', companyDataRef.current);
          return;
        } else {
          setProduct({ ...updatedProduct, productMatchStatus: ProductMatchStatus.ERROR }, true);
        }
      }
    },
    [companyDataRef, setProduct, company, companyId],
  );
  useEffect(() => {
    for (const product of companyData?.products || []) {
      // Segments are never matched to the taxonomy. A stored segment can still carry a PENDING
      // status from a rename, and running that match would put auto-matched attributes back on the
      // segment and save them (PLT-5899).
      if (product.productMatchStatus === ProductMatchStatus.PENDING && product.isSegment !== true) {
        void matchProduct(product);
      }
    }
  }, [companyData, matchProduct]);
}

function useUpdateProduct(
  companyData: PiuDataState | undefined,
  setCompanyData: Dispatch<SetStateAction<PiuDataState | undefined>>,
) {
  const setProduct = useCallback(
    (updatedProduct: PiuProductState, sync: boolean) => {
      setCompanyData(prev => {
        if (!prev) {
          throw Error('Unexpectedly no companyData in setProduct');
        }
        return {
          ...replaceProductInCompanyData(prev, updatedProduct),
          sync,
          blanketStatus: 'draft',
        };
      });
    },
    [setCompanyData],
  );

  const updateProduct = useCallback(
    (updatedProduct: PiuProductState, sync: boolean) => {
      if (!companyData) {
        throw Error('Unexpectedly no companyData');
      }
      const oldProduct = getProductById(companyData, updatedProduct.id);
      if (productChangeTriggersRematch(oldProduct, updatedProduct)) {
        setProduct({ ...updatedProduct, productMatchStatus: ProductMatchStatus.PENDING }, sync);
      } else {
        setProduct({ ...updatedProduct }, sync);
      }
    },
    [setProduct, companyData],
  );

  return updateProduct;
}

/**
 * Autosave-on-change hook used in the `onboarding` wizard flow. The
 * Dashboard "Update products" surface no longer autosaves — see
 * `useProductInputDataState` for the explicit Edit / Save / Cancel
 * paradigm introduced in PLT-4748 PR 2.
 */
export function useSaveProductSetOnChange(
  companyId: string,
  companyData: PiuDataState | undefined,
  mutate: (params: UpsertProductSetMutationParams) => Promise<UpsertProductSetResponse>,
  enabled: boolean,
) {
  const saveProductSet = useCallback(
    async (ds: PiuDataState) => {
      try {
        return await mutate({ piuDataState: ds, companyId });
      } catch (error) {
        console.error('Failed to save product set:', error);
        throw error;
      }
    },
    [mutate, companyId],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (!companyData) {
      return;
    }
    if (!companyData.sync) {
      return;
    }
    if (companyData.blanketStatus === 'publishing') {
      // eslint-disable-next-line no-console
      console.log('skipping saveProductSet because blanket status is publishing', companyData);
      return;
    }
    void saveProductSet(companyData).catch(error => {
      console.error('autosave failed for product set', error);
    });
  }, [enabled, companyData, saveProductSet, companyId]);
}

interface ProductInputDataStateParams {
  companyId: string;
  jobStatus: JobStatus | undefined;
  jobQueryStatus: 'pending' | 'success' | 'error' | 'idle';
  finishStatus: 'pending' | 'success' | 'error' | 'idle';
  mode: 'onboarding' | 'dashboard';
  publishAndComputeResults: () => void;
  // The edits concern a specific variant in the dashboard product editing view. Edits for the specific variant might be disabled, for example, because it has been
  // frozen in a delivery.
  areEditsDisabledForVariant?: boolean;
  /**
   * Edit / Save / Cancel instead of autosave, whatever the segment-editor access. For a host that
   * wants explicit saves on the flat editor: the Edit company dialog (PLT-6280) runs the creation
   * wizard's editor this way. Unset, explicit save comes only with the segment editor on the
   * DMA dashboard, as before.
   */
  explicitSave?: boolean;
  /**
   * With `explicitSave`, open already editing rather than behind the Edit button, for a host whose own
   * button asked to edit (the Upright chat product set card). The edit's baseline is the set as it
   * loads; calling `startEditing` on mount instead would snapshot the set from before it has loaded,
   * so Save would never see a change.
   */
  startInEditMode?: boolean;
}

// NOTE: only use this hook once and pass the state down
export function useProductInputDataState(params: ProductInputDataStateParams): ProductInputUIState {
  const {
    companyId,
    jobStatus,
    jobQueryStatus,
    finishStatus,
    publishAndComputeResults,
    mode,
    explicitSave,
    startInEditMode,
  } = params;
  // PLT-4748 PR 2/3: Explicit Edit / Save / Cancel paradigm for the
  // Dashboard surface; the onboarding wizard stays implicitly in edit
  // mode and continues to autosave.
  //
  // Gated on segment-editor access (PLT-5846): customers holding the
  // `USE_PRODUCT_SEGMENTS` privilege (switched on per user group during the
  // managed rollout), team users via the internal `blanket.plgSegmentEditor`
  // View setting. When access is OFF (every
  // other customer, and team users who haven't opted in) the Dashboard keeps
  // today's autosave behaviour byte-for-byte, so the paradigm shift stays dark.
  const segmentEditorEnabled = useHasProductSegmentsAccess();
  const usesExplicitSave = explicitSave === true || (mode === 'dashboard' && segmentEditorEnabled);
  // Editing state for the explicit-save (segment-editor) flow. In every other
  // mode — onboarding, and dashboard autosave when the segment editor is off —
  // editing is implicitly always on, so `isEditing` is derived as `true` rather
  // than read from this state. We keep the raw state initialised to `false` so
  // that when `usesExplicitSave` becomes true (e.g. a customer's privilege query
  // resolves after first paint) the editor opens in view mode by default, per
  // PLT-5846, instead of getting stuck in edit mode from a stale initial value. `startInEditMode`
  // comes with `explicitSave`, which makes `usesExplicitSave` true from the first render.
  const [isEditingState, setIsEditing] = useState<boolean>(explicitSave === true && startInEditMode === true);
  const isEditing = usesExplicitSave ? isEditingState : true;
  // PLT-4748: persisted per-user / per-company "view" prefs replace the
  // three ephemeral toggles (`showMatching`, `showAttributes`, `viewMode`).
  // The hook reloads from storage when the user / company changes.
  const loggedInUser = useGetLoggedInUser();
  const [viewPrefs, updateViewPrefs] = useSegmentViewPreferences(loggedInUser?.id, companyId);
  const { showMatching, showAttributes, viewMode } = viewPrefs;
  const setShowMatching = useCallback(
    (value: boolean) => {
      updateViewPrefs({ showMatching: value });
    },
    [updateViewPrefs],
  );
  const setShowAttributes = useCallback(
    (value: boolean) => {
      updateViewPrefs({ showAttributes: value });
    },
    [updateViewPrefs],
  );
  const setViewMode = useCallback(
    (value: SegmentViewMode) => {
      updateViewPrefs({ viewMode: value });
    },
    [updateViewPrefs],
  );
  const trackEvent = useEventTracking();

  const { data: company } = useCompany(companyId);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  const [companyData, setCompanyData] = useState<PiuDataState | undefined>();
  const initialDataRef = useRef<PiuDataState | undefined>();
  const storeBaseline = useCallback(
    (data: PiuDataState | undefined) => {
      initialDataRef.current = data ? cloneDeep(data) : undefined;
    },
    [companyId],
  );
  const upsertProductSetMutation = useUpsertProductSetMutation();

  // store product set to state once loaded
  const { refetch, isLoading: isLoadingProductSet } = useLoadAndSetEditableProductSet(
    companyId,
    setCompanyData,
    storeBaseline,
  );

  // Autosave-on-change: kept for the onboarding wizard so leaving the
  // page mid-flow doesn't lose products. In dashboard mode we use the
  // explicit Save button instead (see `saveEdits` below), which calls
  // the same `useUpsertProductSetMutation`.
  useSaveProductSetOnChange(companyId, companyData, upsertProductSetMutation.mutateAsync, !usesExplicitSave);

  // Explicit save mutation status. In onboarding mode the autosave hook
  // is the only caller of `upsertProductSetMutation`, so its `status`
  // already reflects every save; we expose it as `autoSaveStatus`. In
  // dashboard mode the explicit Save button is the only caller, so the
  // same `status` instead reflects the explicit save — exposed as
  // `saveStatus`. Surfacing both fields lets the UI pick the one that
  // matches its mode without re-deriving status from mutation internals.
  const saveStatus = usesExplicitSave ? upsertProductSetMutation.status : 'idle';
  const saveError = usesExplicitSave ? (upsertProductSetMutation.error ?? undefined) : undefined;
  const autoSaveStatus = usesExplicitSave ? 'idle' : upsertProductSetMutation.status;
  // Stable reference (unlike the mutation result object), safe to list as a hook dependency.
  const resetSaveMutation = upsertProductSetMutation.reset;

  const updateProduct = useUpdateProduct(companyData, setCompanyData);

  // (re-match) products when their info changes as needed
  useMatchProducts(companyId, companyData, company, updateProduct);

  const computeResults = useCallback(() => {
    setCompanyData(prev => {
      if (!prev) {
        throw Error('Unexpectedly no companyData in computeResults');
      }
      return {
        ...prev,
        blanketStatus: 'publishing',
        sync: false,
      };
    });
    publishAndComputeResults();
    trackEvent({
      event:
        mode === 'onboarding'
          ? CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_SET_CONFIRMED
          : CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_RECOMPUTATION_STARTED,
    });
  }, [publishAndComputeResults, trackEvent, mode]);

  useEffect(() => {
    if (jobStatus === JobStatus.SUCCESS) {
      // Don't blow away an in-progress editing session if a background
      // recompute finishes while the user is editing. The next refetch
      // happens automatically when they cancel (resetChanges → refetch)
      // or save (saveEdits → refetch after successful upsert).
      if (isEditing && usesExplicitSave) {
        return;
      }
      void refetch();
    }
  }, [jobStatus, refetch, companyId, isEditing, usesExplicitSave]);

  const clearAll = useCallback(() => {
    setCompanyData(_prev => ({
      products: [],
      sync: true,
    }));
    trackEvent({
      event:
        mode === 'onboarding'
          ? CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_ALL_PRODUCTS_DELETED
          : CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_ALL_PRODUCTS_DELETED,
      data: {
        numberOfProducts: '0',
      },
    });
  }, [trackEvent, mode]);

  const resetChanges = useCallback(() => {
    const baseline = initialDataRef.current;
    if (baseline) {
      const baselineSnapshot = cloneDeep(baseline);
      const resolvedBaselineStatus = baselineSnapshot.blanketStatus ?? 'draft';
      setCompanyData({
        ...baselineSnapshot,
        sync: true,
        blanketStatus: resolvedBaselineStatus,
      });
      initialDataRef.current = cloneDeep({ ...baselineSnapshot, blanketStatus: resolvedBaselineStatus });
      return;
    }

    refetch()
      .then(result => {
        const blanket = result.data;
        const resolvedBlanketStatus = blanket?.status ?? 'draft';
        const nextData: PiuDataState = blanket
          ? {
              // This reset fallback only runs for editable sets (read-only sets cannot be edited), so
              // it rebuilds the rows the way the editable load does, segments included.
              products: blanket.products ? buildPiuProducts(blanket.products, false) : [],
              sync: true,
              blanketStatus: resolvedBlanketStatus,
            }
          : { ...getEmptyData(), sync: true, blanketStatus: 'draft' };
        setCompanyData(nextData);
        initialDataRef.current = cloneDeep(nextData);
      })
      .catch(error => {
        console.error('Failed to reset product set:', error);
        if (!initialDataRef.current) {
          setCompanyData({ ...getEmptyData(), sync: true });
        }
      });
  }, [refetch]);

  const duplicateProduct = useCallback((product: PiuProductState) => {
    const newProduct = {
      ...product,
      id: `${product.id}-copy`,
      title: `${product.title} (copy)`,
      description: `${product.description}`,
    };
    setCompanyData(prev => {
      if (!prev) {
        throw Error('Unexpectedly no companyData in duplicateProduct');
      }
      return {
        ...prev,
        sync: true,
        blanketStatus: 'draft',
        products: [...prev.products, newProduct],
      };
    });
  }, []);

  const deleteProduct = useCallback(
    (product: PiuProductState) => {
      setCompanyData(prev => {
        if (!prev) {
          throw Error('Unexpectedly no companyData in deleteProduct');
        }
        // Re-parent the deleted node's direct children onto its own parent (the grandparent;
        // `null` when the deleted node was top-level) BEFORE removing it, so deleting a mid-level
        // segment lifts its children one level up instead of orphaning them with a dangling
        // `parentId` (which would render them as spurious top-level rows). Harmless for leaf
        // deletes — there are no children to re-parent (PLT-5799).
        const newParentId = product.parentId ?? null;
        return {
          ...prev,
          sync: true,
          blanketStatus: 'draft',
          products: prev.products
            .map(p => (p.parentId === product.id ? { ...p, parentId: newParentId } : p))
            .filter(p => p.id !== product.id),
        };
      });
      trackEvent({
        event:
          mode === 'onboarding'
            ? CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_DELETED
            : CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_DELETED,
        data: {
          productName: product.title,
          productDescription: product.description,
        },
      });
    },
    [trackEvent, mode],
  );

  // PLT-5690: hierarchy-view drag-and-drop reparenting. Updates only the
  // `parentId` of the moved node (leaf or segment). The DnD layer proactively
  // suppresses invalid drops; this re-validates (cycle + `MAX_SEGMENT_DEPTH`)
  // defensively via the same guard the backend enforces, and no-ops rather than
  // persisting a corrupt tree. Only wired in the gated Dashboard editor.
  const reparentProduct = useCallback(
    (productId: string, newParentId: string | null) => {
      if (!usesExplicitSave) return;
      setCompanyData(prev => {
        if (!prev) {
          throw Error('Unexpectedly no companyData in reparentProduct');
        }
        const product = prev.products.find(p => p.id === productId);
        if (!product) return prev;
        if ((product.parentId ?? null) === newParentId) return prev;
        const nextProducts = prev.products.map(p => (p.id === productId ? { ...p, parentId: newParentId } : p));
        try {
          assertHierarchyValid(nextProducts);
        } catch {
          return prev;
        }
        return { ...prev, sync: true, blanketStatus: 'draft', products: nextProducts };
      });
      trackEvent({
        event: CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_EDITED,
        data: {
          oldProductName: productId,
          productDescription: `reparent to ${newParentId ?? 'top-level'}`,
        },
      });
    },
    [usesExplicitSave, trackEvent],
  );

  const addProduct = useCallback(
    // `parentId` (PLT-5691): when set, the new product is created as a child of
    // that node (the Hierarchy-view "Add child product" flow); omitted / null
    // keeps today's top-level add. The caller is expected to only offer the
    // child affordance for valid parents, but we still re-validate below.
    (title: string, description: string, revenueShare: number | undefined, parentId: string | null = null) => {
      const newProduct = {
        ...createEmptyProduct(title, description, revenueShare),
        productMatchStatus: ProductMatchStatus.PENDING,
        parentId,
      };
      setCompanyData(prev => {
        if (!prev) {
          throw Error('Unexpectedly no companyData in addProduct');
        }
        // A top-level add (no parent) goes to the FRONT so it lands at the top of the list, where
        // the user is looking after clicking "+ Add product" (PLT-5799). Child adds keep appending
        // so a new child slots after its existing siblings under the parent.
        const nextProducts = parentId === null ? [newProduct, ...prev.products] : [...prev.products, newProduct];
        try {
          assertHierarchyValid(nextProducts);
        } catch {
          return prev;
        }
        return {
          ...prev,
          sync: true,
          blanketStatus: 'draft',
          products: nextProducts,
        };
      });
      trackEvent({
        event:
          mode === 'onboarding'
            ? CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_ADDED
            : CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_ADDED,
        data: {
          productName: title,
          productDescription: description,
        },
      });
    },
    [trackEvent, mode],
  );

  // PLT-5691: create a new segment / business-unit container. Segments carry no
  // taxonomy mapping or matching (they persist as flagged containers — see the
  // `isSegment` save path), so unlike `addProduct`
  // there is no dialog / match step: the node is created immediately with a
  // default title and renamed inline. Gated to the explicit-save editor and
  // re-validated (depth / cycle) defensively, mirroring `reparentProduct`.
  const addSegment = useCallback(
    (parentId: string | null) => {
      if (!usesExplicitSave) return;
      setCompanyData(prev => {
        if (!prev) {
          throw Error('Unexpectedly no companyData in addSegment');
        }
        const newSegment: PiuProductState = {
          id: uuid(),
          title: 'New segment',
          description: '',
          isSegment: true,
          parentId,
          revenueShare: undefined,
          productMatchResult: undefined,
        };
        // A top-level segment (no parent) goes to the FRONT so it appears at the top of the list
        // right after the user clicks "+ Add segment" (PLT-5799); a child segment keeps appending
        // after its existing siblings under the parent.
        const nextProducts = parentId === null ? [newSegment, ...prev.products] : [...prev.products, newSegment];
        try {
          assertHierarchyValid(nextProducts);
        } catch {
          return prev;
        }
        return { ...prev, sync: true, blanketStatus: 'draft', products: nextProducts };
      });
      trackEvent({
        event: CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_ADDED,
        data: {
          productName: 'New segment',
          productDescription: `segment under ${parentId ?? 'top-level'}`,
        },
      });
    },
    [usesExplicitSave, trackEvent],
  );

  // PLT-5707: "Add parent segment" from a product row inserts a new segment
  // as the PARENT of that product (Roni QA) — the intuitive reading of the
  // action — rather than as a child under it. The new segment takes the
  // product's current parent, and the product is reparented beneath the new
  // segment. Re-validated (depth / cycle) defensively like the other hierarchy
  // mutations, so inserting a level that would breach the depth cap no-ops.
  const addParentSegment = useCallback(
    (productId: string) => {
      if (!usesExplicitSave) return;
      setCompanyData(prev => {
        if (!prev) {
          throw Error('Unexpectedly no companyData in addParentSegment');
        }
        const product = prev.products.find(p => p.id === productId);
        if (!product) return prev;
        const newSegment: PiuProductState = {
          id: uuid(),
          title: 'New segment',
          description: '',
          isSegment: true,
          parentId: product.parentId ?? null,
          revenueShare: undefined,
          productMatchResult: undefined,
        };
        const nextProducts = [
          ...prev.products.map(p => (p.id === productId ? { ...p, parentId: newSegment.id } : p)),
          newSegment,
        ];
        try {
          assertHierarchyValid(nextProducts);
        } catch {
          return prev;
        }
        return { ...prev, sync: true, blanketStatus: 'draft', products: nextProducts };
      });
      trackEvent({
        event: CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_ADDED,
        data: {
          productName: 'New segment',
          productDescription: `parent segment above ${productId}`,
        },
      });
    },
    [usesExplicitSave, trackEvent],
  );

  // PLT-4748 PR 2: derived dirty flag — true only while editing and
  // when the working `companyData` differs from the snapshot captured
  // on `startEditing`. Onboarding mode never compares because its
  // baseline is reset on every autosave; we short-circuit to `false`.
  const hasUnsavedEdits = useMemo(() => {
    if (!usesExplicitSave) return false;
    if (!isEditing) return false;
    if (!companyData) return false;
    const baseline = initialDataRef.current;
    if (!baseline) return false;
    return !isEqual(stripVolatileFields(companyData), stripVolatileFields(baseline));
  }, [usesExplicitSave, isEditing, companyData]);

  // Whether the results are out of date w.r.t. the current product set — drives the "Recompute
  // results" nudge + button. Compares the *delta* from the baseline, not the sticky `blanketStatus`
  // flag: a change that is made and then reverted nets to nothing and must NOT nag a recompute
  // (PLT-5799 — Roni). A `'draft'` status carried over from BEFORE this edit session (saved but not
  // yet recomputed) always needs a recompute regardless of what happens in-session.
  const needsRecompute = useMemo(() => {
    if (!companyData) return false;
    const baselineStatus = isEditing ? initialDataRef.current?.blanketStatus : companyData.blanketStatus;
    if (baselineStatus === 'draft') return true;
    // Otherwise recompute is needed only when the current set genuinely differs from the baseline
    // (`hasUnsavedEdits` is content-only and already false outside an edit session).
    return hasUnsavedEdits;
  }, [companyData, isEditing, hasUnsavedEdits]);

  // Browser-level unsaved-changes prompt — fires on tab close, page
  // refresh, or any other beforeunload trigger. We attach the listener
  // only while there are unsaved edits so benign navigations don't
  // prompt. Mirrors the FS-input pattern (PR #6875).
  useEffect(() => {
    if (!hasUnsavedEdits) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
    };
  }, [hasUnsavedEdits]);

  const startEditing = useCallback(() => {
    if (!usesExplicitSave) return;
    // Snapshot the currently-loaded data so Cancel can restore it and
    // `hasUnsavedEdits` has a baseline to compare against.
    storeBaseline(companyData);
    // A failed save from an earlier session must not greet the user as they start a new one.
    resetSaveMutation();
    setIsEditing(true);
  }, [usesExplicitSave, companyData, storeBaseline, resetSaveMutation]);

  const cancelEditing = useCallback(() => {
    if (!usesExplicitSave) return;
    // Restore the baseline snapshot taken on `startEditing`. Reusing
    // `resetChanges` keeps the reload path (and the recovery branch
    // when the baseline ref is missing) in one place.
    resetChanges();
    resetSaveMutation();
    setIsEditing(false);
  }, [usesExplicitSave, resetChanges, resetSaveMutation]);

  // `onSaved` runs after a successful save + refetch — used by the
  // top-right Recompute action's "Save & recompute" path to chain the
  // recompute onto the save's success rather than racing a `useEffect`.
  const saveEdits = useCallback(
    (onSaved?: () => void) => {
      if (!usesExplicitSave) return;
      if (!companyData) return;
      // Mark the data as ready to persist; the explicit upsert below
      // takes the working `companyData` as-is. We keep the `sync` flag
      // semantics intact so a later autosave path in another mode still
      // works — but here we trigger the upsert directly rather than
      // relying on the autosave hook (which is disabled in dashboard mode).
      const payload: PiuDataState = { ...companyData, sync: true };
      upsertProductSetMutation.mutate(
        { piuDataState: payload, companyId },
        {
          onSuccess: () => {
            // Re-establish the baseline as the just-saved state so a
            // subsequent edit session starts clean. We also exit edit mode
            // and refetch to pick up any server-side normalisations
            // (e.g. id remapping by the save handler).
            storeBaseline(payload);
            setIsEditing(false);
            void refetch();
            onSaved?.();
          },
          onError: error => {
            // The user-facing message is rendered from `saveError`; this keeps the full error
            // (status, stack) available for debugging.
            console.error('Failed to save product set:', error);
          },
        },
      );
    },
    [usesExplicitSave, companyData, companyId, upsertProductSetMutation, storeBaseline, refetch],
  );

  const updateProductWithSync = useCallback(
    (updatedProduct: PiuProductState) => {
      const oldProduct = companyData ? getProductById(companyData, updatedProduct.id) : undefined;
      updateProduct(updatedProduct, true);
      trackEvent({
        event:
          mode === 'onboarding'
            ? CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_EDITED
            : CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_EDITED,
        data: {
          oldProductName: oldProduct?.title,
          newProductName: updatedProduct.title,
          productDescription: updatedProduct.description,
        },
      });
    },
    [updateProduct, trackEvent, companyData, mode],
  );

  // PLT-5109: only the dashboard product-input page renders non-PIU sets read-only. The onboarding
  // review step also runs through this hook but loads a freshly generated (company-mapping) draft
  // that the user must be able to edit, so it must never be treated as read-only.
  const readOnly = mode === 'dashboard' && companyData?.readOnly === true;

  return {
    data: companyData,
    readOnly,
    updateProduct: updateProductWithSync,
    showMatching,
    setShowMatching,
    showAttributes,
    setShowAttributes,
    viewMode,
    setViewMode,
    duplicateProduct,
    deleteProduct,
    reparentProduct,
    clearAll,
    resetChanges,
    addProduct,
    addSegment,
    addParentSegment,
    publishAndComputeResults: computeResults,
    autoSaveStatus,
    finishStatus,
    jobStatus,
    jobQueryStatus,
    disabled: companyData?.blanketStatus === 'publishing' || params.areEditsDisabledForVariant === true || readOnly,
    isLoadingProductSet,
    isEditing,
    hasUnsavedEdits,
    needsRecompute,
    startEditing,
    saveEdits,
    cancelEditing,
    saveStatus,
    saveError,
  };
}

/**
 * Strip non-meaningful fields before comparing two `PiuDataState`s for
 * dirty-tracking. The transient `sync` flag flips during autosave /
 * explicit save round-trips and the `productMatchStatus` flips through
 * `PENDING → RUNNING → SUCCESS` during async LLM matching — neither
 * represents a user-visible edit, so we ignore them so the Save button
 * doesn't light up the instant a match finishes after baseline capture.
 *
 * `blanketStatus` is deliberately NOT compared: any edit flips it to
 * `'draft'` and it never flips back, so including it would keep the set
 * looking dirty even after the user reverts every change (PLT-5799 — Roni:
 * dirty/recompute state must reflect the delta from the baseline, not
 * whether anything was ever touched). Dirtiness is purely about product
 * content.
 */
function stripVolatileFields(data: PiuDataState): unknown {
  return {
    products: data.products.map(p => ({
      ...p,
      productMatchStatus: undefined,
    })),
  };
}

function getProductById(data: PiuDataState, id: string): PiuProductState {
  const product = data.products.find(p => p.id === id);
  if (!product) {
    throw Error(`Product with id=${id} not found`);
  }
  return product;
}

function productChangeTriggersRematch(oldProduct: PiuProductState, updatedProduct: PiuProductState) {
  // Segments are never matched, so renaming one must not queue a match. The match wrote an
  // auto-matched IPR blob onto the segment, which made the stored row read as an ordinary product
  // once it had no children (PLT-5899).
  if (updatedProduct.isSegment === true) {
    return false;
  }
  return updatedProduct.title !== oldProduct.title || updatedProduct.description !== oldProduct.description;
}

function replaceProductInCompanyData(data: PiuDataState, updatedProduct: PiuProductState) {
  return {
    ...data,
    products: data.products.map(product => (product.id === updatedProduct.id ? updatedProduct : product)),
  };
}

function createEmptyProduct(title: string, description: string, revenueShare: number | undefined): PiuProductState {
  return {
    id: uuid(), // the ids are used only in the UI
    title,
    description,
    revenueShare,
    manualIprEdits: {},
    ipr: undefined,
    productMatchResult: undefined,
  };
}
